import moment from 'moment-timezone';
import { Record, List } from 'immutable';
import _ from 'lodash';

import config from '../config';
import { sum } from '../helpers/Immutable';
import Station from './Station';
import trainTime from '../lib/TrainTime';
import OrderTrip from './order/Trip';
import AmendedTicket from './amend/Ticket';
import DeliveryOption from './DeliveryOption';
import { Brands, OrderStates } from '../constants/AppConstants';
import OrderFulfilment from './order/Fulfilment';
import OrderPayments from './order/Payments';
import OrderSundry from './order/Sundry';
import OrderDiscount from './OrderDiscount';
import { validateOrderJourneys, HandoffState } from '../helpers/DelayRepayOrderHandoff';
import { construct, fields, resolve, resolveAll } from '../helpers/LinkMapper';
import { SmartcardErrorBlocking, SmartCardErrorBlockingForLoggedIn, SmartcardErrorNonBlocking } from '../constants/AppConstants';
import FeatureFlagStore from '../stores/FeatureFlagStore';
import UserStore from "../stores/UserStore";
import fpUtils from '../utils/fp-utils';
import ChangeoverTicket from './changeover/Ticket';

const Order = new Record({
    id: null,
    email: null,
    brand: null,
    charity: null,
    customerId: null,
    customerName: null,
    expiry: null,
    trips: new List(),
    delivery: new OrderFulfilment(),
    collectionReference: null,
    amendedTickets: new List(),
    changeoverTickets: new List(),
    state: null,
    discount: new OrderDiscount(),
    totalPrice: null,
    leftToPay: null,
    amendPriceDiff: null,
    nlc: null,
    creationDate: null,
    purchaseDate: null,
    lastUpdated: null,
    availableDeliveryOptions: new List(),
    payments: null,
    changeoverPayments: null,
    telephoneNumber: null,
    isPartialRefund: false,
    isFullRefund: false,
    isChangeoverRefund: false,
    alreadyPaidForRefund: false,
    refundCanBeAttemptedOnline: false,
    refundCanBeAttemptedOffline: false,
    refundsLocked: false,
    sundries: new List(),
    isInterop: false,
    channel: null,
    totalRewardPoints: null,
    rewardPointUid: null,
    rewardName: null,
    promotionalDiscount: null
});

Object.assign(Order.prototype, {
    isGuestOrder() {
        return !this.customerId;
    },

    isSmartcard() {
        return !!this.trips.size && this.trips.some(trip => trip.isSmartcard());
    },

    isMultiSmartcard() {
        return this.isSmartcard() && (this.trips.size > 1 || this.getTickets().size > 1);
    },

    isSmartcardWithProductDelivery() {
        if (this.isSmartcard()) {
            const smartTrip = this.trips.find(trip => trip.isSmartcard());
            return smartTrip.getTickets().some(ticket => !ticket.getSmartcardNumber());
        }
        return false;
    },

    isETicketDeliveryAndCanAmendETickets () {
        return this.delivery.isETicket() && FeatureFlagStore.get('enableETicketAmends');
    },

    isETicketByPostDelivery () {
        return FeatureFlagStore.get('enableETicketByPost') && this.delivery.isETicketByPost();
    },

    canAmend() {
        return (
            (this.delivery.isCollection() || this.isETicketDeliveryAndCanAmendETickets() || this.isETicketByPostDelivery()) &&
            !this.hasPaypalPayment() &&
            !this.hasAmazonPayPayment() &&
            !this.isAmending() &&
            !this.isCollected() &&
            this.trips.some(x => x.canAmend())
        );
    },

    hasDaySaveTrip() {
        return this.trips.some(trip => trip.getDaySaveFare());
    },

    hasRoversRangersTrip() {
        return this.trips.some(trip => trip.getRoversRangersFare());
    },

    hasSeasonTrip() {
        return this.trips.some(trip => trip.getSeasonFare());
    },

    hasSmartCarnetTrip () {
        return this.trips.some((trip) => trip.getSmartCarnetFare());
    },

    hasPartialFulfilment () {
        return this.delivery;
    },

    getSeasonTrips() {
        return this.trips.filter(trip => trip.getSeasonFare());
    },

    getSmartCarnetTrips () {
        return this.trips.filter((trip) => trip.getSmartCarnetFare());
    },

    numberOfSeasonTrips () {
        return this.trips.filter((trip) => trip.getSeasonFare()).size;
    },

    hasFulfilment({disableAddressCheck = false, linkedSmartcards = false} = {}) {
        return (
            this.delivery &&
            this.delivery.isValid(this.trips, {disableAddressCheck, linkedSmartcards}, this.getSmartcardOption())
        );
    },

    getTodOption() {
        return this.availableDeliveryOptions.find(deliveryOption => deliveryOption.isCollection());
    },

    getPendingAmendedTickets() {
        return this.amendedTickets.filter(x => !x.amended);
    },

    getSmartcardOption() {
        return this.availableDeliveryOptions.find(deliveryOption => deliveryOption.isSmartcard());
    },

    getSTicketOption() {
        return this.availableDeliveryOptions.find(deliveryOption => deliveryOption.isSTicket());
    },

    isTooSoonToCollectOnSmartcard () {
        const enableForcedAppCollectionForTwoHourCollectionWindow = FeatureFlagStore.get('enableForcedAppCollectionForTwoHourCollectionWindow');

        if (enableForcedAppCollectionForTwoHourCollectionWindow) {
            return false;
        }

        const smartcardOption = this.getSmartcardOption();

        if (!smartcardOption) {
            return false;
        }

        const { errors } = smartcardOption;

        return errors && errors.some((err => SmartcardErrorNonBlocking.APP_COLLECTION_ONLY in err));
    },

    insufficientActiveSmartcardsToCompleteLinkingOrder () {
        const enableSmartcardAssociations = FeatureFlagStore.get('enableSmartcardAssociations');

        const smartcardOption = this.getSmartcardOption();

        if (!enableSmartcardAssociations || !smartcardOption || !this.delivery.isSmartcard()) {
            return false;
        }

        const { errors } = smartcardOption;

        if (errors && errors.some((err => SmartCardErrorBlockingForLoggedIn.NOT_ENOUGH_ACTIVE_SMARTCARDS_ERROR in err))) {
            return true;
        }

        return false;
    },

    isSmartcardDeliveryOnly() {
        return this.availableDeliveryOptions.size === 1 && !!this.getSmartcardOption();
    },

    isSTicketDeliveryOnly() {
        return this.availableDeliveryOptions.size === 1 && !!this.getSTicketOption();
    },

    tooManyTicketsForNonLinkedSmartOrder(ignoreDeliveryMethods) {
        if (!ignoreDeliveryMethods && !this.delivery.isSmartcard()) {
            return false;
        }

        const enableSmartcardAssociations = FeatureFlagStore.get('enableSmartcardAssociations');

        if (!enableSmartcardAssociations) {
            const tooManyTicketsForSmartcardOnlyDelivery =
                this.getTickets().size > 1 &&
                (ignoreDeliveryMethods ? true : this.isSmartcardDeliveryOnly());

            return tooManyTicketsForSmartcardOnlyDelivery;
        }

        return false;
    },

    smartcardCantBeDeliveredInTime(associatedSmartcards, smartcard) {
        const smartcardOption = this.getSmartcardOption();

        if (!smartcardOption || !this.delivery.isSmartcard()) {
            return false;
        }

        const { errors } = smartcardOption;

        const errorKeys = [
            SmartcardErrorBlocking.CANT_DELIVER_IN_TIME
        ];

        // errors is an array, but seems to only ever contain one error object
        if (errors && errors.some(err => fpUtils.objectHasAnyKeyInList(err, errorKeys))) {
            return true;
        }

        const isUserLoggedIn = UserStore.getCurrent().isLoggedIn();

        if (isUserLoggedIn && smartcardOption.isDeliverableOnTime === false && (!smartcard && (associatedSmartcards && associatedSmartcards.size < 1))) {
            return true;
        }

        return false;
    },

    smartcardInvalid() {
        const smartcardOption = this.getSmartcardOption();

        if (!smartcardOption || !this.delivery.isSmartcard()) {
            return false;
        }

        const { errors } = smartcardOption;

        const errorKeys = [
            SmartcardErrorBlocking.PENDING_SMARTCARD,
            SmartcardErrorBlocking.EXPIRED_SMARTCARD,
            SmartcardErrorBlocking.INVALID_SMARTCARD
        ];

        // errors is an array, but seems to only ever contain one error object
        if (errors && errors.some(err => fpUtils.objectHasAnyKeyInList(err, errorKeys))) {
            return true;
        }

        return false;
    },

    smartcardTypeFareAgeMismatch() {
        const smartcardOption = this.getSmartcardOption();

        if (!smartcardOption || !this.delivery.isSmartcard()) {
            return false;
        }

        const { errors } = smartcardOption;

        const errorKeys = [
            SmartcardErrorBlocking.NOT_CORRECT_AGE,
        ];

        // errors is an array, but seems to only ever contain one error object
        if (errors && errors.some(err => fpUtils.objectHasAnyKeyInList(err, errorKeys))) {
            return true;
        }

        return false;
    },

    smartcardHasPendingProducts() {
        /*
            We only block the purchase of further products on a Smartcard
            that has pending (uncollected) products for GTR brands
        */
        if (!FeatureFlagStore.get('enablePendingActionlistSmartcardDeliveryBlocking')) {
            return false;
        }

        const smartcardOption = this.getSmartcardOption();

        if (!smartcardOption || !this.delivery.isSmartcard()) {
            return false;
        }

        const { errors } = smartcardOption;

        const errorKeys = [
            SmartcardErrorBlocking.PENDING_ACTIONLIST,
        ];

        // errors is an array, but seems to only ever contain one error object
        if (errors && errors.some(err => fpUtils.objectHasAnyKeyInList(err, errorKeys))) {
            return true;
        }

        return false;
    },

    canBeFulfilledToSmartcard(options = {}) {
        const {
            includesChildPreencode,
            ignoreNewSmartcardDeliveryTime,
            ignoreDeliveryMethods
        } = options;

        return !this.isTooSoonToCollectOnSmartcard()
            && !includesChildPreencode
            && !this.tooManyTicketsForNonLinkedSmartOrder(ignoreDeliveryMethods)
            && !this.smartcardTypeFareAgeMismatch()
            && !this.smartcardInvalid()
            && !this.smartcardHasPendingProducts()
            && (!ignoreNewSmartcardDeliveryTime ? !this.smartcardCantBeDeliveredInTime() : true);
    },

    hasSmartcardDeliveryErrors() {
        const smartcardOption = this.getSmartcardOption();

        if (!smartcardOption) {
            return false;
        }

        const { errors } = smartcardOption;
        return !!errors;
    },

    hasSTicketDeliveryErrors() {
        const sTicketOption = this.getSTicketOption();

        if (!sTicketOption) {
            return false;
        }

        const { errors } = sTicketOption;
        return !!errors;
    },

    canBeFulfilled() {
        if (!this.availableDeliveryOptions.size) {
            return false;
        }

        if (this.isSmartcard()) {
            return !!this.getSmartcardOption();
        }

        if (this.isSmartcardDeliveryOnly() && (!this.canBeFulfilledToSmartcard() || this.hasSmartcardDeliveryErrors())) {
            return false;
        }

        if (this.isSTicketDeliveryOnly() && this.hasSTicketDeliveryErrors()) {
            return false;
        }

        /*
            if Smartcard and sTicket are the only delivery options and both have errors,
            then the order cannot be fulfilled
        */
        if (
            !!this.getSmartcardOption() &&
            !!this.getSTicketOption() &&
            this.availableDeliveryOptions.size === 2 &&
            this.hasSmartcardDeliveryErrors() &&
            this.hasSTicketDeliveryErrors()
        ) {
            return false;
        }

        return true;
    },

    isRefundable() {
        return this.canRefundOnline() || this.canRefundOffline();
    },

    canRefundOnline() {
        // TODO: [CORE-16632]: This is a temporary solution to block refund for changeover tickets
        return this.refundCanBeAttemptedOnline && this.changeoverTickets.size === 0;
    },

    canRefundOffline() {
        return this.refundCanBeAttemptedOffline;
    },

    isRefundsLocked() {
        return this.refundsLocked;
    },

    hasCollectionWindowEnded() {
        const now = trainTime();
        const latestCollectionDateBasedOnTrips = this.getLatestCollectionDate();
        const latestCollectionDateBasedOnWindow = this.getCollectionStartDate()
            .clone()
            .add(this.delivery.collectionWindowDays, 'days');

        const latestCollectionDate = latestCollectionDateBasedOnTrips.isBefore(
            latestCollectionDateBasedOnWindow,
        )
            ? latestCollectionDateBasedOnTrips
            : latestCollectionDateBasedOnWindow;

        return latestCollectionDate.isBefore(now);
    },

    getCollectionStartDate() {
        return this.delivery && this.delivery.collectionStartDate
            ? this.delivery.collectionStartDate
            : this.getEarliestCollectionDate();
    },

    getSmartcardCollectionLocation() {
        return (
            this.getIn(['delivery', 'collectionLocation']) ||
            this.getSmartcardOption().suggestedCollectionPoint
        );
    },

    getEarliestCollectionDate() {
        const now = trainTime();
        const depart = this.trips.first().getEarliestTime();
        const earliestDepart = depart
            .clone()
            .subtract(this.delivery.collectionWindowDays - 1, 'days');
        const earliestDepartDate = now.isBefore(earliestDepart) ? earliestDepart : now;
        return earliestDepartDate.startOf('day');
    },

    getLatestCollectionDate() {
        const now = trainTime();
        const latestCollectionDate = this.trips.first().getEarliestTime();
        return now.isBefore(latestCollectionDate) ? latestCollectionDate : now;
    },

    getDiscountCode() {
        return this.getIn(['discount', 'code']);
    },

    getTotalDiscount() {
        return this.getIn(['discount', 'fixedAmount']) > 0
            ? this.discount.get('fixedAmount')
            : null;
    },

    getTotalPrice() {
        return this.totalPrice;
    },

    getOriginalTotalPrice() {
        return this.getTotalPriceWithDelivery();
    },

    getTotalPriceWithDelivery() {
        return (
            this.getTripsPrice() +
            this.getDeliveryPrice() -
            this.getTotalDiscount() -
            this.getDiscountCodeValue() +
            this.getBookingFeePrice()
        );
    },

    getTripsPrice() {
        return this.get('trips').reduce((totalPrice, trip) => totalPrice + trip.getTotalPrice(), 0);
    },

    getRefundAmount() {
        if (this.hasAlternativePayment()) {
            return this.getAlternativePaymentRefundAmount();
        } else {
            return this.getTotalRefundAmount();
        }
    },

    getTrip(id) {
        return this.trips.find(trip => trip.id === id || trip.getCleanId() === id);
    },

    updateTrip(trip, nextTrip) {
        const next = this.trips.set(
            this.trips.findIndex(x => x.id === trip.id),
            nextTrip,
        );
        return this.set('trips', next);
    },

    getTripIds() {
        return this.trips.map(trip => trip.getCleanId());
    },

    setDeliveryOption(option) {
        return this.set('delivery', this.delivery.setOption(option));
    },

    getDeliveryPrice() {
        return this.getIn(['delivery', 'price']);
    },

    getDeliveryMethodName() {
        return this.getIn(['delivery', 'displayName']);
    },

    hasDeliveryAddress() {
        const {delivery} = this;
        if (!delivery || !delivery.isPostalDelivery()) {
            return false;
        }

        const address = delivery.getAddress();
        return address && address.isComplete();
    },

    isPostalDelivery() {
        return this.delivery && this.delivery.isPostalDelivery();
    },

    isSmartcardCollection() {
        return this.delivery && this.delivery.isSmartcard();
    },

    getDeliveryAddress() {
        return this.delivery.getAddress();
    },

    getFirstOrigin() {
        const sorted = this.trips.sort((a, b) => {
            return a.getEarliestTime().isBefore(b.getEarliestTime()) ? -1 : 1;
        });

        const earliest = sorted.first();

        if (!earliest) {
            return new Station();
        } else {
            return earliest.getFirstOrigin();
        }
    },

    isPaid() {
        return _.includes(
            [
                OrderStates.COMPLETE,
                OrderStates.PENDING,
                OrderStates.UNDER_REVIEW,
                OrderStates.FULFILLED,
                OrderStates.FRAUD,
                OrderStates.REFUNDED,
            ],
            this.state,
        );
    },

    isUnderReview() {
        return _.includes([OrderStates.UNDER_REVIEW, OrderStates.FRAUD], this.state);
    },

    isAmending() {
        return OrderStates.AMENDING === this.state;
    },

    isComplete() {
        return OrderStates.COMPLETE === this.state;
    },

    isCollectAtStation() {
        return this.delivery.isCollection();
    },

    isPrintAtHome() {
        return this.delivery.isPrintAtHome();
    },

    isETicket() {
        return this.delivery.isETicket();
    },

    isSTicket() {
        return this.delivery.isSTicket();
    },

    isCollectable() {
        return this.isCollectAtStation() && this.trips.some(trip => trip.isUnprinted());
    },

    isCollected() {
        return this.isCollectAtStation() && this.trips.every(trip => trip.isPrinted());
    },

    isEmpty() {
        return this.trips.size === 0;
    },

    isCurrentBrand() {
        return (
            _.isEqual(this.brand, config.get('brand')) ||
            config.get('enableAllowRefundsOnOtherBrands')
        );
    },

    brandIsGTR() {
        return Boolean(Object.keys(Brands).find((key) => Brands[key] === this.brand));
    },

    hasExpired() {
        return this.expiry ? this.expiry.isBefore() : false;
    },

    hasRecentlyExpired() {
        return this.expiry ? this.expiry.isAfter(trainTime().subtract(2, 'hours')) : false;
    },

    setAvailableDeliveryOptions(deliveryOptions) {
        let updatedOrder = this.set('availableDeliveryOptions', deliveryOptions);

        // If delivery options are available
        if (deliveryOptions.size) {
            // Set it to the first available without an error (like smartcard unavailable)
            return updatedOrder.setDeliveryOption(
                deliveryOptions.find(option =>
                    option.getFirstAvailableDeliveryOption(this.delivery, deliveryOptions)
                )
            );
        }

        // If there are no options, clear the selected
        return updatedOrder.setDeliveryOption(null);
    },

    getFaresIncludedInFavourites (withDelivery = false) {
        return this.get('trips').reduce((acc, trip) =>
            trip.includesFavouriteFare(withDelivery)
                ? acc.concat(trip.getFaresIncludedInFavourites(withDelivery))
                : acc
            , new List()
        );
    },

    includesFavouriteFare (withDelivery = false) {
        return this.getFaresIncludedInFavourites(withDelivery).size > 0;
    },

    hasCardPayment (type = 'payments') {
        return this.getCardPayments(type).size > 0;
    },

    getCardPaymentAmount () {
        const cardPayment = this.getCardPayment();
        return cardPayment && cardPayment.amount || 0;
    },

    getCardPayment () {
        return this.getCardPayments().first();
    },

    getCardPayments (type = 'payments') {
        return this[type] && this[type].getCards() || new List();
    },

    hasLatestVoucherPayment() {
        return this.payments.hasLatestVoucherPayment();
    },

    hasLatestCardPayment() {
        return this.payments.hasLatestCardPayment();
    },

    getLatestCardPaymentDetails() {
        return this.payments.getLatestCardDetails();
    },

    getLatestVoucherPayment() {
        if (!this.hasVoucherPayment()) {
            return null;
        }
        return this.payments.getLatestVoucherPayment();
    },

    getLatestCardPayment() {
        if (!this.hasCardPayment()) {
            return null;
        }
        return this.payments.getLatestCardPayment();
    },

    hasVoucherPayment(type = 'payments') {
        return this.getIn([type, 'warrants'], new List()).size > 0;
    },

    hasPromoCode() {
        return !!this.getPromoCode();
    },

    getPromoCode() {
        return this.promotionalDiscount;
    },

    checkIfThisPromotionalDiscountHasAlreadyApplied(code) {
        return this.getPromoCode() &&
            this.getPromoCode().promoCode &&
            this.getPromoCode().promoCode.toLowerCase() === code.toLowerCase();
    },

    checkIfThisPromoCodeHasAlreadyApplied(code) {
        const tickets = this.getTickets();
        const ticketWithTheSamePromoCode = tickets.find(ticket => ticket.discount && ticket.promoCode === code);
        return Boolean(ticketWithTheSamePromoCode);
    },

    getVoucherPaymentAmount(type = 'payments') {
        return sum(this[type].warrants || new List(), 'amount');
    },

    getCardPaymentsTotal() {
        return sum(this.getCardPayments() || new List(), 'amount');
    },

    hasAmazonPayPayment(type = 'payments') {
        return this.getIn([type, 'amazonPay'], new List()).size > 0;
    },

    hasPaypalPayment(type = 'payments') {
        return this.getIn([type, 'paypal'], new List()).size > 0;
    },

    hasGooglePayPayment(type = 'payments') {
        return this.getIn([type, 'googlePay'], new List()).size > 0;
    },

    hasApplePayPayment(type = 'payments') {
        return this.getIn([type, 'applePay'], new List()).size > 0;
    },

    getAmazonPayPayment(type = 'payments') {
        return this.getIn([type, 'amazonPay', 0]);
    },

    getPaypalPayment(type = 'payments') {
        return this.getIn([type, 'paypal', 0]);
    },

    getApplePayPayment(type = 'payments') {
        return this.getIn([type, 'applePay', 0]);
    },

    getPaypalPaymentAmount(type = 'payments') {
        return this.getIn([type, 'paypal', 0, 'amount'], 0);
    },

    getGooglePayPayment(type = 'payments') {
        return this.getIn([type, 'googlePay', 0]);
    },

    getGooglePayPaymentAmount(type = 'payments') {
        return this.getIn([type, 'googlePay', 0, 'amount'], 0);
    },

    getApplePayPaymentAmount(type = 'payments') {
        return this.getIn([type, 'applePay', 0, 'amount'], 0);
    },

    getPaymentRefundAmount(type) {
        return sum(this.getIn(['payments', type], new List()), 'amountRefunded');
    },

    getVoucherRefundAmount() {
        return this.getPaymentRefundAmount('warrants');
    },

    getCardRefundAmount () {
        return this.getPaymentRefundAmount('cards') + this.getPaymentRefundAmount('hostedCards');
    },

    getPaypalRefundAmount() {
        return this.getPaymentRefundAmount('paypal');
    },

    getAmazonPayRefundAmount() {
        return this.getPaymentRefundAmount('amazonPay');
    },

    getAlternativePayment() {
        return this.getIn(['payments', 'alternative', 0]);
    },

    getTotalRefundAmount() {
        return (
            this.getCardRefundAmount() +
            this.getPaypalRefundAmount() +
            this.getAmazonPayRefundAmount() +
            this.getVoucherRefundAmount()
        );
    },

    getAlternativePaymentRefundAmount() {
        return this.getIn(['payments', 'alternative', 0, 'amount'], 0);
    },

    getAlternativePaymentType() {
        return this.getIn(['payments', 'alternative', 0, 'paymentType'], 0);
    },

    // this method does not consider eTickets:
    hasTooSoonToCollect() {
        return this.trips.some(trip => trip.isTooSoonToCollect());
    },

    collectionLocationEqualTo(station) {
        const collectionStation = this.getIn(['delivery', 'collectionLocation']);
        return station && collectionStation && station.nlc === collectionStation.nlc;
    },

    hasRefunds() {
        return this.isFullRefund || this.isPartialRefund;
    },

    hasRefundableTicket() {
        return this.trips.some(t => t.hasRefundableTicket());
    },

    hasRefundablePaymentType() {
        // for now only allow card/voucher payments to be refunded
        return this.hasCardPayment() || (this.hasVoucherPayment() && !this.hasPaypalPayment());
    },

    hasAmendablePaymentType() {
        // same as for refunds but separate in case one or the other
        // changes
        return this.hasCardPayment() || (this.hasVoucherPayment() && !this.hasPaypalPayment());
    },

    hasDownloadableTickets() {
        return (
            this.isPaid() &&
            this.isPrintAtHome() &&
            this.trips.some(t => t.hasDownloadableTickets())
        );
    },

    getProducts() {
        return this.trips.map(trip => trip.getProducts()).flatten(1);
    },

    getTickets() {
        return this.getProducts()
            .map(fare => fare.tickets)
            .flatten(1);
    },

    hasChildTickets() {
        return this.getTickets().some(({ children }) => children > 0);
    },

    getFareByTicketId(id) {
        return this.getFares().find(fare => fare.tickets.find(t => t.id === id));
    },

    getFares() {
        return this.getProducts().map(fare => fare);
    },

    hasFlexiFares() {
        return this.getFares().some(x => x.isFlexi());
    },

    arePassengersReady() {
        return !this.trips
            .filter(trip => trip.needsPassengers())
            .some(trip => !trip.hasPassengers());
    },

    hasAmendedTickets() {
        return this.amendedTickets.size > 0;
    },

    hasChangeoverTickets() {
        return this.changeoverTickets.size > 0;
    },

    getChangeoverTrips() {
        return this.changeoverTickets.map(({ changeoverTrip }) => changeoverTrip);
    },

    isNewTicket(id) {
        return [...this.amendedTickets, ...this.changeoverTickets].some(fare => fare.newTicket === id);
    },

    getOldFareFromNewTicketId(id) {
        if (!this.isNewTicket(id)) {
            return null;
        }
        return [...this.amendedTickets, ...this.changeoverTickets].find(amended => amended.isNewTicket(id)).getOldFare();
    },

    hasReachedDiscountLimit(discount) {
        if (!discount || !discount.maxProducts) {
            return false;
        }

        const dicountAppliedCount = this.trips.count(trip => trip.hasDiscount(discount));
        return dicountAppliedCount >= discount.maxProducts;
    },

    hasAlternativePayment() {
        return this.getIn(['payments', 'alternative', 0]);
    },

    hasRailcard1617() {
        return this.getFares().some(fare => fare.hasRailcard1617());
    },

    hasRailcardJCP() {
        return this.getFares().some(fare => fare.hasRailcardJCP());
    },

    hasDisabledPeopleRailcard() {
        return this.getFares().some((fare) => fare.hasDisabledPeopleRailcard());
    },

    getBookingFeePrice() {
        const bookingFeeSundry = this.sundries.find(sundry => sundry.isBookingFee());
        return bookingFeeSundry ? bookingFeeSundry.price : 0;
    },

    getDiscountCodeValue() {
        const discountCodeSundry = this.sundries.find(sundry => sundry.isDiscountCode());
        return discountCodeSundry ? discountCodeSundry.price : 0;
    },

    canClaimDelayRepay() {
        return validateOrderJourneys(this, config.get('tocGroup')).some(
            validation => validation.status === HandoffState.CLAIMABLE,
        );
    },

    numAdultOrChildTickets(adults) {
        return this.getProducts()
        .map(fare => fare.tickets.reduce((acc, ticket) => acc + Number(adults ? ticket.adults > 0 : ticket.children > 0), 0))
        .reduce((acc, numTickets) => acc + numTickets, 0);
    },
});

Order.fromJSON = function(json) {
    const trips = resolveAll(json.trips)
        .as(OrderTrip)
        .sort((orderTripA, orderTripB) => {
            return orderTripA.id > orderTripB.id;
        });
    const tickets = trips.map(trip => trip.getTickets()).flatten(true);
    const refunds = tickets.filter(ticket => ticket.isRefunded);
    const isChangeoverRefund = tickets.some(ticket => ticket.isChangeoverRefund);
    const amendedTickets = resolveAll(json.amendedTickets).as(AmendedTicket);
    const changeoverTickets = resolveAll(json.changeoverTickets).as(ChangeoverTicket);

    return construct(Order, json, {
        id: fields.number,
        expiry: resolve.with(moment),
        trips: trips,
        state: fields.string,
        brand: fields.string,
        nlc: fields.string,
        amendedTickets,
        changeoverTickets,
        email: fields.string,
        charity: json.charity,
        customerId: fields.number,
        customerName: fields.string,
        collectionReference: fields.string,
        discount: json.discount ? resolve.as(OrderDiscount) : new OrderDiscount(),
        totalPrice: fields.number,
        leftToPay: fields.number,
        amendPriceDiff: fields.number,
        creationDate: resolve.with(trainTime),
        purchaseDate: resolve.with(trainTime),
        lastUpdated: resolve.with(trainTime),
        delivery: json.delivery ? resolve.as(OrderFulfilment) : new OrderFulfilment(),
        availableDeliveryOptions: resolveAll.as(DeliveryOption),
        payments: resolve(json.payments || {}).as(OrderPayments),
        changeoverPayments: resolve(json.changeoverPayments || {}).as(OrderPayments),
        telephoneNumber: fields.string,
        isFullRefund: refunds.size > 0 && tickets.size === refunds.size,
        isPartialRefund: refunds.size > 0 && refunds.size < tickets.size,
        isChangeoverRefund,
        refundCanBeAttemptedOnline: fields.bool,
        refundCanBeAttemptedOffline: fields.bool,
        refundsLocked: fields.bool,
        alreadyPaidForRefund: resolve(json.alreadyPaidForRefund).with(Boolean),
        amazonPayOrderReferenceId: fields.string,
        sundries: resolveAll(json.sundries).as(OrderSundry),
        channel: fields.string,
        isInterop: fields.bool,
        totalRewardPoints: fields.number,
        rewardPointUid: fields.string,
        rewardName: fields.string,
        promotionalDiscount: resolve.with(Object),
    });
};

export default Order;



// WEBPACK FOOTER //
// ./src/records/Order.js